import React, { useCallback, useEffect, useState } from 'react';
import PageHeader from '@/components/dashboard/PageHeader';
import ThemeToggle from '@/components/ThemeToggle';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useSearchParams } from 'react-router-dom';
import AccountLifecycleCard from '@/components/settings/AccountLifecycleCard';
import AccountExportCard from '@/components/settings/AccountExportCard';
import { useAccountState, useExportState } from '@/lib/accountData';
import { useResolvedImageUrl } from '@/components/estimates/ProposalImage';
import {
  AlertCircle,
  Building2,
  HardHat,
  Search,
  Settings as SettingsIcon,
  Bell,
  Save,
  Plus,
  Trash2,
  Loader2,
  ShieldCheck,
  Languages,
  Palette,
  Image as ImageIcon,
  Upload,
  X,
  QrCode,
  KeyRound,
  Copy,
  RefreshCw,
  MessageSquare,
  Link as LinkIcon,
  Eye,
  EyeOff,
  CheckCircle2,
} from 'lucide-react';
import {
  loadAllSettings,
  upsertSettingsByKey,
  SETTINGS_KEYS,
  DEFAULT_DEFAULTS,
} from '@/lib/settingsData';
import {
  fetchPortalRoster,
  provisionCrewMember,
  rotateCrewPin,
  buildCrewSmsLink,
  buildCrewSmsBody,
  buildCrewSmsDeepLink,
} from '@/lib/crewPortalData';
import {
  normalizeCrewMember,
  saveCrewRoster,
  isActiveCrew,
  CREW_STATUSES,
} from '@/lib/crewRoster';
import EmployeeProfileSheet, {
  STATUS_TONE,
} from '@/components/settings/EmployeeProfileSheet';
import WriteupsSheet from '@/components/settings/WriteupsSheet';
import HoursExportDialog from '@/components/settings/HoursExportDialog';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, setLanguage, getLanguage } from '@/i18n';
import {
  deletePortalPhoto,
  isStorageUrl,
} from '@/lib/portalStorage';

/* Downscale an image file to a compact PNG data URL (preserves transparency).
 * The company logo is embedded directly in settings rather than stored in the
 * private portal-photos bucket — small, no signing, and it renders everywhere
 * including the anonymous client portal (where signed URLs don't work). */
const scaleImageToDataUrl = (file, maxDim = 512) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read file.'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Could not load image.'));
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/png'));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
import { QrCodeBlock, buildCompanyVCard } from '@/components/common/QrCodeBlock';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  DEFAULT_BRANDING,
  HEADER_FONT_OPTIONS,
  ACCENT_PRESETS,
  LOGO_PLACEMENT_OPTIONS,
  tierAllows,
  requiredTierFor,
  TIER_LABEL,
} from '@/lib/branding';
import { Lock } from 'lucide-react';
import DailyRemindersCard from '@/components/settings/DailyRemindersCard';

const SectionHeader = ({ Icon, eyebrow, title, subtitle, action }) => (
  <div className="flex items-start justify-between gap-3 mb-4">
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 rounded-md border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 flex items-center justify-center shrink-0">
        {Icon ? <Icon size={16} className="text-[hsl(var(--primary))]" /> : null}
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {eyebrow}
        </div>
        <div className="text-base font-semibold text-foreground tracking-tight">{title}</div>
        {subtitle ? (
          <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>
        ) : null}
      </div>
    </div>
    {action ? <div className="shrink-0">{action}</div> : null}
  </div>
);

const LoadingShell = () => (
  <div className="space-y-4" data-testid="settings-skeleton">
    {Array.from({ length: 3 }).map((_, i) => (
      <Card key={i} className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-4 sm:p-5 space-y-3">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-2/3" />
        </CardContent>
      </Card>
    ))}
  </div>
);

/* ------------------------------------------------------------------ *
 * ChangePasswordCard
 *
 * In-app password change. Requires the user to be signed in (SettingsPage is
 * gated behind ProtectedRoute). For safety we re-authenticate with the
 * current password BEFORE calling supabase.auth.updateUser — this prevents
 * someone with a hijacked live session from rotating the password without
 * knowing the current one.
 * ------------------------------------------------------------------ */
const MIN_PASSWORD_LEN_CHG = 8;

const ChangePasswordCard = () => {
  const { t } = useTranslation();
  const { user, signIn, updatePassword } = useAuth();

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!currentPw) {
      setError(t('auth.change_pw_current_required'));
      return;
    }
    if (newPw.length < MIN_PASSWORD_LEN_CHG) {
      setError(t('auth.reset_pw_too_short', { min: MIN_PASSWORD_LEN_CHG }));
      return;
    }
    if (newPw === currentPw) {
      setError(t('auth.change_pw_same_as_old'));
      return;
    }
    if (newPw !== confirmPw) {
      setError(t('auth.reset_pw_mismatch'));
      return;
    }
    if (!user?.email) {
      setError(t('auth.change_pw_no_user'));
      return;
    }

    setSubmitting(true);
    // Re-auth with current password first (proves possession).
    const reauth = await signIn({ email: user.email, password: currentPw });
    if (reauth.error) {
      setSubmitting(false);
      setError(t('auth.change_pw_current_wrong'));
      return;
    }

    const { error: upErr } = await updatePassword(newPw);
    setSubmitting(false);

    if (upErr) {
      setError(upErr.message || t('auth.reset_generic_error'));
      return;
    }

    setCurrentPw('');
    setNewPw('');
    setConfirmPw('');
    toast.success(t('auth.change_pw_success_toast'));
  };

  return (
    <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]" data-testid="change-password-card">
      <CardContent className="p-4 sm:p-5 space-y-4">
        <SectionHeader
          Icon={ShieldCheck}
          eyebrow={t('auth.security_eyebrow')}
          title={t('auth.change_pw_title')}
          subtitle={t('auth.change_pw_subtitle')}
        />

        <form onSubmit={handleSubmit} className="space-y-4 max-w-md" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="chg-current-pw" className="text-xs text-muted-foreground">
              {t('auth.current_password')}
            </Label>
            <div className="relative">
              <Input
                id="chg-current-pw"
                type={showCurrent ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                className="h-11 pr-11 bg-[hsl(var(--muted))] border-border"
                data-testid="chg-current-pw-input"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                aria-label={showCurrent ? t('auth.hide_password') : t('auth.show_password')}
                className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--primary))]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 transition-colors"
                data-testid="chg-current-pw-toggle"
              >
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="chg-new-pw" className="text-xs text-muted-foreground">
              {t('auth.new_password')}
            </Label>
            <div className="relative">
              <Input
                id="chg-new-pw"
                type={showNew ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="••••••••"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="h-11 pr-11 bg-[hsl(var(--muted))] border-border"
                data-testid="chg-new-pw-input"
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                aria-label={showNew ? t('auth.hide_password') : t('auth.show_password')}
                className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--primary))]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 transition-colors"
                data-testid="chg-new-pw-toggle"
              >
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground/80">
              {t('auth.pw_hint', { min: MIN_PASSWORD_LEN_CHG })}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="chg-confirm-pw" className="text-xs text-muted-foreground">
              {t('auth.confirm_password')}
            </Label>
            <Input
              id="chg-confirm-pw"
              type={showNew ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="••••••••"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              className="h-11 bg-[hsl(var(--muted))] border-border"
              data-testid="chg-confirm-pw-input"
            />
          </div>

          {error ? (
            <Alert
              variant="default"
              className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
              data-testid="chg-error-message"
            >
              <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          ) : null}

          <div className="flex items-center gap-3 pt-1">
            <Button
              type="submit"
              disabled={submitting}
              className="h-11 min-w-[10rem] bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/85 font-semibold tracking-[0.12em] uppercase"
              data-testid="chg-submit-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('auth.change_pw_saving')}
                </>
              ) : (
                <>
                  <ShieldCheck className="h-4 w-4 mr-2" />
                  {t('auth.change_pw_submit')}
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

/* ----------------------------------------------------------------------------
 * Proposal branding (white-label) — tier-gated controls in the Company tab.
 * Locked controls render disabled with a tier badge + upgrade hint.
 * ------------------------------------------------------------------------- */
const BrandingLock = ({ feature }) => {
  const need = requiredTierFor(feature);
  return (
    <span
      className="inline-flex items-center gap-1 ml-2 rounded-full border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] text-[hsl(var(--primary))] align-middle"
      title={`Available on the ${TIER_LABEL[need]} plan`}
    >
      <Lock size={9} />
      {TIER_LABEL[need]}
    </span>
  );
};

const BrandingSection = ({ company, setCompany, tier }) => {
  const branding = { ...DEFAULT_BRANDING, ...(company?.branding || {}) };
  const setBranding = (patch) =>
    setCompany({ ...company, branding: { ...branding, ...patch } });

  const canFont = tierAllows(tier, 'header_font');
  const canColor = tierAllows(tier, 'accent_color');
  const canPlacement = tierAllows(tier, 'logo_placement');

  return (
    <div
      className="sm:col-span-2 pt-4 mt-2 border-t border-border space-y-4"
      data-testid="company-branding-section"
    >
      <div className="space-y-1">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[hsl(var(--primary))]">
          Proposal branding
        </div>
        <p className="text-xs text-muted-foreground">
          White-label how your client-facing proposals look. Current plan:{' '}
          <span className="text-foreground/80 font-medium">
            {TIER_LABEL[tier] || tier}
          </span>
          . Your <span className="text-foreground/80">tagline</span> (above) is
          used as the proposal saying, and your{' '}
          <span className="text-foreground/80">logo</span> comes from the upload
          at the top.
        </p>
      </div>

      {/* Accent color — Foreman+ */}
      <div className="space-y-2">
        <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          Accent color
          {!canColor ? <BrandingLock feature="accent_color" /> : null}
        </Label>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            disabled={!canColor}
            onClick={() => setBranding({ accent_color: '' })}
            className={`h-8 px-3 rounded-md border text-xs ${
              !branding.accent_color
                ? 'border-[hsl(var(--primary))]/50 bg-[hsl(var(--primary))]/10 text-foreground'
                : 'border-border bg-[hsl(var(--muted))] text-muted-foreground'
            } ${!canColor ? 'opacity-50 cursor-not-allowed' : ''}`}
            data-testid="branding-accent-default"
          >
            Default gold
          </button>
          {ACCENT_PRESETS.map((p) => {
            const active =
              (branding.accent_color || '').toLowerCase() === p.hex.toLowerCase();
            return (
              <button
                key={p.hex}
                type="button"
                disabled={!canColor}
                title={p.label}
                onClick={() => setBranding({ accent_color: p.hex })}
                className={`h-8 w-8 rounded-full border-2 transition-transform ${
                  active
                    ? 'border-foreground scale-110'
                    : 'border-border/60'
                } ${!canColor ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}`}
                style={{ backgroundColor: p.hex }}
                data-testid={`branding-accent-${p.hex.replace('#', '')}`}
              />
            );
          })}
          <label
            className={`inline-flex items-center gap-1.5 text-xs ${
              canColor ? 'text-muted-foreground' : 'opacity-50'
            }`}
          >
            <span>Custom</span>
            <input
              type="color"
              disabled={!canColor}
              value={branding.accent_color || '#b8860b'}
              onChange={(e) => setBranding({ accent_color: e.target.value })}
              className="h-8 w-10 rounded border border-border bg-transparent p-0.5 disabled:cursor-not-allowed"
              data-testid="branding-accent-custom"
            />
          </label>
        </div>
      </div>

      {/* Header font — Foreman+ */}
      <div className="space-y-2 max-w-xs">
        <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          Header font
          {!canFont ? <BrandingLock feature="header_font" /> : null}
        </Label>
        <Select
          value={branding.header_font}
          onValueChange={(v) => setBranding({ header_font: v })}
          disabled={!canFont}
        >
          <SelectTrigger
            className="h-10 bg-[hsl(var(--muted))] border-border"
            data-testid="branding-font-trigger"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[hsl(var(--card))] border-border">
            {HEADER_FONT_OPTIONS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Logo placement — Boss only */}
      <div className="space-y-2 max-w-xs">
        <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          Logo placement
          {!canPlacement ? <BrandingLock feature="logo_placement" /> : null}
        </Label>
        <Select
          value={branding.logo_placement}
          onValueChange={(v) => setBranding({ logo_placement: v })}
          disabled={!canPlacement}
        >
          <SelectTrigger
            className="h-10 bg-[hsl(var(--muted))] border-border"
            data-testid="branding-placement-trigger"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[hsl(var(--card))] border-border">
            {LOGO_PLACEMENT_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { tier } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'company';
  const { state: account, setState: setAccount } = useAccountState();
  const { exp: accountExport, setExp: setAccountExport } = useExportState(!!account?.is_owner);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [currentLang, setCurrentLang] = useState(getLanguage());

  const [company, setCompany] = useState(null);
  const [crew, setCrew] = useState([]);
  const [defaults, setDefaults] = useState(null);
  const [notifications, setNotifications] = useState(null);

  // Portal roster (crew_portals): { [crew_id]: { code, access, active, ... } }
  const [portalRoster, setPortalRoster] = useState({});
  // Per-row "busy" indicator for PIN actions
  const [pinBusyId, setPinBusyId] = useState(null);
  // Employee profile sheet (Phase 1 onboarding fields)
  const [profileSheetOpen, setProfileSheetOpen] = useState(false);
  const [profileTarget, setProfileTarget] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  // Per-employee Phase 2 surfaces
  const [writeupsTarget, setWriteupsTarget] = useState(null);
  const [writeupsOpen, setWriteupsOpen] = useState(false);
  const [hoursTarget, setHoursTarget] = useState(null);
  const [hoursOpen, setHoursOpen] = useState(false);
  // Show inactive (laid_off / terminated / inactive) employees in the list
  const [showInactive, setShowInactive] = useState(false);
  // Crew roster search (name / role) — scales the list for bigger outfits.
  const [crewSearch, setCrewSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await loadAllSettings();
      setCompany(all.company);
      // Normalize every crew entry so the new operational fields (phone,
      // email, hire_date, emergency contact, status, …) always exist on
      // state — eliminates `undefined` handling in every consumer.
      setCrew((Array.isArray(all.crew) ? all.crew : []).map(normalizeCrewMember));
      setDefaults(all.defaults);
      setNotifications(all.notifications);
      // Roster is tolerated to fail (e.g. RLS hides settings to non-admin)
      try {
        const roster = await fetchPortalRoster();
        setPortalRoster(roster || {});
      } catch (_) {
        setPortalRoster({});
      }
    } catch (e) {
      setError(e?.message || 'Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (key, value, label) => {
    setSavingKey(key);
    try {
      await upsertSettingsByKey(key, value);
      toast.success(`${label || 'Settings'} saved`);
    } catch (e) {
      toast.error(e?.message || 'Failed to save settings.');
    } finally {
      setSavingKey(null);
    }
  };

  /* -------------------- Company Info -------------------- */
  const [logoUploading, setLogoUploading] = useState(false);
  // Sign the private-bucket logo for the preview (stored public URL 404s).
  const logoSrc = useResolvedImageUrl({ url: company?.logo_url, path: company?.logo_path });

  const handleSaveCompany = (e) => {
    e?.preventDefault?.();
    save(SETTINGS_KEYS.COMPANY, company, 'Company profile');
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      // Clean up any legacy Storage-hosted logo, best-effort.
      const prev = company?.logo_url;
      // Embed the logo directly as a compact data URL (no private bucket, no
      // signing) so it renders everywhere — including the anon client portal.
      const dataUrl = await scaleImageToDataUrl(file, 512);
      const next = {
        ...company,
        logo_url: dataUrl,
        logo_path: '',
        logo_uploaded_at: new Date().toISOString(),
      };
      setCompany(next);
      await upsertSettingsByKey(SETTINGS_KEYS.COMPANY, next);
      if (prev && isStorageUrl(prev)) deletePortalPhoto(prev).catch(() => {});
      toast.success('Logo uploaded.');
    } catch (err) {
      toast.error(err?.message || 'Logo upload failed.');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoRemove = async () => {
    const prev = company?.logo_url;
    const next = {
      ...company,
      logo_url: '',
      logo_path: '',
      logo_uploaded_at: '',
    };
    setCompany(next);
    try {
      await upsertSettingsByKey(SETTINGS_KEYS.COMPANY, next);
      if (prev && isStorageUrl(prev)) deletePortalPhoto(prev).catch(() => {});
      toast.success('Logo removed.');
    } catch (err) {
      toast.error(err?.message || 'Failed to remove logo.');
    }
  };

  /* -------------------- Crew Rates -------------------- */
  const handleAddCrew = () => {
    setCrew((cur) => [
      ...cur,
      normalizeCrewMember({
        id: `c-${Date.now()}`,
        name: '',
        role: 'Crew',
        rate: 0,
        status: 'active',
      }),
    ]);
  };
  const handleRemoveCrew = (id) => {
    setCrew((cur) => cur.filter((c) => c.id !== id));
  };

  /**
   * Save the full crew roster to settings.crew_rates AND sync the portal
   * active flag in settings.crew_portals so a single status field gates
   * both clock-in (via the Crew Clock active roster) and portal PIN login.
   */
  const handleSaveCrew = async () => {
    setSavingKey(SETTINGS_KEYS.CREW_RATES);
    try {
      // 1) Persist roster via the unified helper so every subscriber
      //    (Crew Clock, Schedule, etc.) sees the change instantly.
      await saveCrewRoster(crew);

      // 2) Mirror status -> portal active flag (non-blocking; admin-only
      //    write, tolerated to fail under non-admin RLS).
      try {
        const portals = { ...(portalRoster || {}) };
        let portalDirty = false;
        for (const c of crew) {
          if (!portals[c.id]) continue;
          const shouldBeActive = isActiveCrew(c);
          if (Boolean(portals[c.id].active) !== shouldBeActive) {
            portals[c.id] = { ...portals[c.id], active: shouldBeActive };
            portalDirty = true;
          }
        }
        if (portalDirty) {
          await upsertSettingsByKey('crew_portals', portals);
          setPortalRoster(portals);
        }
      } catch (_) {
        /* tolerated */
      }

      toast.success(t('settings.crew.savedToast'));
    } catch (e) {
      toast.error(e?.message || t('settings.crew.saveFailed'));
    } finally {
      setSavingKey(null);
    }
  };

  /**
   * Persist a single edited employee record from the profile sheet.
   * Replaces the matching entry in the local crew state then triggers
   * a full-roster save (so portal sync logic runs the same way).
   */
  const handleProfileSave = async (next) => {
    setProfileSaving(true);
    try {
      const merged = (crew || []).map((c) => (c.id === next.id ? next : c));
      setCrew(merged);
      await saveCrewRoster(merged);
      // Mirror portal active flag for this one member
      try {
        if (portalRoster?.[next.id]) {
          const shouldBeActive = isActiveCrew(next);
          if (Boolean(portalRoster[next.id].active) !== shouldBeActive) {
            const portals = {
              ...portalRoster,
              [next.id]: { ...portalRoster[next.id], active: shouldBeActive },
            };
            await upsertSettingsByKey('crew_portals', portals);
            setPortalRoster(portals);
          }
        }
      } catch (_) {
        /* tolerated */
      }
      toast.success(
        t('settings.crew.profileSavedToast', { name: next.name })
      );
      setProfileSheetOpen(false);
    } catch (e) {
      toast.error(e?.message || t('settings.crew.saveFailed'));
    } finally {
      setProfileSaving(false);
    }
  };

  const openProfileSheet = (member) => {
    setProfileTarget(member);
    setProfileSheetOpen(true);
  };

  /* -------- Crew PIN provisioning + SMS link -------- */
  const copyToClipboard = async (text, label) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers / non-secure contexts
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success(t('settings.crew_pin.copied', { label }));
    } catch (e) {
      toast.error(t('settings.crew_pin.copy_failed'));
    }
  };

  const handleProvisionCrewPin = async (member) => {
    if (!member?.name || !member.name.trim()) {
      toast.error(t('settings.crew_pin.name_required'));
      return;
    }
    setPinBusyId(member.id);
    try {
      const res = await provisionCrewMember({
        crewId: member.id,
        name: member.name,
        role: member.role || 'Crew',
        rate: Number(member.rate) || 0,
        access: 'full',
      });
      if (!res.ok) {
        toast.error(
          res.error === 'admin_required'
            ? t('settings.crew_pin.admin_required')
            : res.error || t('settings.crew_pin.provision_failed')
        );
        return;
      }
      // Refresh roster + reflect canonical crew_id back into the rates list
      const roster = await fetchPortalRoster();
      setPortalRoster(roster || {});
      setCrew((cur) =>
        cur.map((c) =>
          c.id === member.id
            ? {
                id: res.crew_id || c.id,
                name: res.crew_name || c.name,
                role: res.role || c.role,
                rate: Number(res.rate) || Number(c.rate) || 0,
              }
            : c
        )
      );
      toast.success(t('settings.crew_pin.provisioned', { code: res.code }));
    } catch (e) {
      toast.error(e?.message || t('settings.crew_pin.provision_failed'));
    } finally {
      setPinBusyId(null);
    }
  };

  const handleRotateCrewPin = async (member) => {
    setPinBusyId(member.id);
    try {
      const res = await rotateCrewPin(member.id);
      if (!res.ok) {
        toast.error(
          res.error === 'crew_not_provisioned'
            ? t('settings.crew_pin.not_provisioned_yet')
            : res.error || t('settings.crew_pin.rotate_failed')
        );
        return;
      }
      const roster = await fetchPortalRoster();
      setPortalRoster(roster || {});
      toast.success(t('settings.crew_pin.rotated', { code: res.code }));
    } catch (e) {
      toast.error(e?.message || t('settings.crew_pin.rotate_failed'));
    } finally {
      setPinBusyId(null);
    }
  };

  /* -------------------- Defaults -------------------- */
  const handleSaveDefaults = () => {
    save(SETTINGS_KEYS.DEFAULTS, defaults, 'Defaults');
  };

  /* -------------------- Notifications -------------------- */
  const handleSaveNotifications = () => {
    save(SETTINGS_KEYS.NOTIFICATIONS, notifications, 'Notifications');
  };

  return (
    <div data-testid="settings-page">
      <PageHeader
        eyebrow={t('settings.eyebrow')}
        title={t('settings.title')}
        subtitle={t('settings.subtitle')}
      />

      {error ? (
        <Alert
          className="mb-5 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
          data-testid="settings-error"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      {loading ? (
        <LoadingShell />
      ) : (
        <Tabs defaultValue={initialTab} className="w-full" data-testid="settings-tabs">
          <TabsList className="bg-[hsl(var(--muted))] border border-border mb-4 overflow-x-auto no-scrollbar">
            <TabsTrigger value="company" className="text-xs uppercase tracking-[0.16em]">
              {t('settings.tab_company')}
            </TabsTrigger>
            <TabsTrigger value="crew" className="text-xs uppercase tracking-[0.16em]">
              {t('settings.tab_crew')}
            </TabsTrigger>
            <TabsTrigger value="defaults" className="text-xs uppercase tracking-[0.16em]">
              {t('settings.tab_defaults')}
            </TabsTrigger>
            <TabsTrigger value="notifications" className="text-xs uppercase tracking-[0.16em]">
              {t('settings.tab_notifications')}
            </TabsTrigger>
            <TabsTrigger value="security" className="text-xs uppercase tracking-[0.16em]" data-testid="settings-tab-security">
              {t('settings.tab_security')}
            </TabsTrigger>
            <TabsTrigger value="appearance" className="text-xs uppercase tracking-[0.16em]" data-testid="settings-tab-appearance">
              {t('settings.tab_appearance', { defaultValue: 'Appearance' })}
            </TabsTrigger>
            <TabsTrigger value="language" className="text-xs uppercase tracking-[0.16em]" data-testid="settings-tab-language">
              {t('settings.tab_language')}
            </TabsTrigger>
            {account?.is_owner ? (
              <TabsTrigger value="account" className="text-xs uppercase tracking-[0.16em]" data-testid="settings-tab-account">
                {t('account.tab')}
              </TabsTrigger>
            ) : null}
          </TabsList>

          {/* COMPANY ----------------------------------- */}
          <TabsContent value="company" className="space-y-4">
            {/* Logo upload */}
            <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
              <CardContent className="p-4 sm:p-5">
                <SectionHeader
                  Icon={ImageIcon}
                  eyebrow="Branding"
                  title="Company logo"
                  subtitle="Pulled through to estimate and invoice headers. PNG or JPG, up to 25 MB."
                />
                <div className="flex flex-col sm:flex-row items-start gap-4 mt-3">
                  <div
                    className="h-24 w-24 sm:h-28 sm:w-28 rounded-md border border-[hsl(var(--hairline))] bg-[#0f0e0c] flex items-center justify-center overflow-hidden flex-shrink-0"
                    data-testid="company-logo-preview"
                  >
                    {company?.logo_url ? (
                      <img
                        src={logoSrc}
                        alt="Company logo"
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <div className="text-center">
                        <ImageIcon className="h-6 w-6 mx-auto text-muted-foreground mb-1" aria-hidden />
                        <span className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
                          No logo
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 w-full">
                    <div className="flex flex-wrap items-center gap-2">
                      <label
                        htmlFor="company-logo-input"
                        className="inline-flex items-center justify-center h-9 px-4 rounded-md text-xs uppercase tracking-[0.16em] cursor-pointer bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                        data-testid="company-logo-upload-label"
                      >
                        {logoUploading ? (
                          <Loader2 size={14} className="mr-2 animate-spin" />
                        ) : (
                          <Upload size={14} className="mr-2" />
                        )}
                        {company?.logo_url ? 'Replace logo' : 'Upload logo'}
                      </label>
                      <input
                        id="company-logo-input"
                        data-testid="company-logo-input"
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/heic,image/heif"
                        className="sr-only"
                        disabled={logoUploading}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleLogoUpload(f);
                          if (e.target) e.target.value = '';
                        }}
                      />
                      {company?.logo_url ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleLogoRemove}
                          disabled={logoUploading}
                          className="h-9 border-[hsl(var(--destructive))]/30 text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10 uppercase tracking-[0.16em] text-xs"
                          data-testid="company-logo-remove"
                        >
                          <X size={14} className="mr-2" />
                          Remove
                        </Button>
                      ) : null}
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Recommended: square or wide PNG with a transparent or dark
                      background. The logo appears on every estimate and invoice
                      automatically once saved.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact QR */}
            <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
              <CardContent className="p-4 sm:p-5">
                <SectionHeader
                  Icon={QrCode}
                  eyebrow="Share"
                  title="Company contact QR"
                  subtitle="Scan to add this contractor's contact card directly to a phone."
                />
                <div className="mt-3">
                  <QrCodeBlock
                    value={buildCompanyVCard(company || {})}
                    title="VCard"
                    subtitle="Includes name, phone, email, address, website, and license number."
                    downloadName={`${(company?.name || 'company').replace(/\s+/g, '-').toLowerCase()}-contact.png`}
                    size={180}
                    testId="company-contact-qr"
                  >
                    <div className="text-xs text-muted-foreground space-y-1">
                      {company?.name ? <div className="text-foreground font-medium">{company.name}</div> : null}
                      {company?.phone ? <div>📞 {company.phone}</div> : null}
                      {company?.email ? <div>✉️ {company.email}</div> : null}
                      {company?.website ? <div>🌐 {company.website}</div> : null}
                      {!company?.name && !company?.phone && !company?.email ? (
                        <div className="italic">Fill in company info below to populate the QR.</div>
                      ) : null}
                    </div>
                  </QrCodeBlock>
                </div>
              </CardContent>
            </Card>

            {/* Company info form */}
            <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
              <CardContent className="p-4 sm:p-5">
                <SectionHeader
                  Icon={Building2}
                  eyebrow="Profile"
                  title="Company info"
                  subtitle="Used on proposals, invoices, and receipts."
                />
                <form onSubmit={handleSaveCompany} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Display name" testId="company-name">
                    <Input
                      value={company?.name || ''}
                      onChange={(e) => setCompany({ ...company, name: e.target.value })}
                      className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
                      data-testid="company-name-input"
                    />
                  </Field>
                  <Field label="Legal name">
                    <Input
                      value={company?.legal_name || ''}
                      onChange={(e) => setCompany({ ...company, legal_name: e.target.value })}
                      className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
                    />
                  </Field>
                  <Field label="Phone">
                    <Input
                      value={company?.phone || ''}
                      onChange={(e) => setCompany({ ...company, phone: e.target.value })}
                      className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
                      data-testid="company-phone-input"
                    />
                  </Field>
                  <Field label="Email">
                    <Input
                      type="email"
                      value={company?.email || ''}
                      onChange={(e) => setCompany({ ...company, email: e.target.value })}
                      className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
                      data-testid="company-email-input"
                    />
                  </Field>
                  <Field label="Address" full>
                    <Input
                      value={company?.address || ''}
                      onChange={(e) => setCompany({ ...company, address: e.target.value })}
                      className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
                    />
                  </Field>
                  <Field label="Website">
                    <Input
                      value={company?.website || ''}
                      onChange={(e) => setCompany({ ...company, website: e.target.value })}
                      className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
                    />
                  </Field>
                  <Field label="Tagline">
                    <Input
                      value={company?.tagline || ''}
                      onChange={(e) => setCompany({ ...company, tagline: e.target.value })}
                      className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
                    />
                  </Field>
                  <Field label="License #">
                    <Input
                      value={company?.license || ''}
                      onChange={(e) => setCompany({ ...company, license: e.target.value })}
                      className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
                      data-testid="company-license-input"
                    />
                  </Field>
                  <Field label="Insurance / policy">
                    <Input
                      value={company?.insurance || ''}
                      onChange={(e) => setCompany({ ...company, insurance: e.target.value })}
                      className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
                    />
                  </Field>

                  {/* White-label proposal branding — tier-gated controls. */}
                  <BrandingSection
                    company={company}
                    setCompany={setCompany}
                    tier={tier}
                  />

                  {/* Phase 26 — reusable proposal-clause defaults. These are
                      copied into each NEW estimate on creation. Editing an
                      estimate's copy does NOT mutate these defaults; editing
                      these defaults does NOT retroactively touch existing
                      estimates. */}
                  <div className="sm:col-span-2 pt-4 mt-2 border-t border-border space-y-1">
                    <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[hsl(var(--primary))]">
                      Standard proposal clauses
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Pre-filled into every new estimate. Each estimate keeps
                      its own editable copy — changes here apply only to future
                      estimates.
                    </p>
                  </div>
                  <Field label="Default warranty" full>
                    <Textarea
                      rows={3}
                      value={company?.default_warranty || ''}
                      onChange={(e) => setCompany({ ...company, default_warranty: e.target.value })}
                      placeholder="E.g. 1-year workmanship warranty on all labor; manufacturer warranty on materials."
                      className="bg-[hsl(var(--muted))] border-border text-foreground resize-none text-sm"
                      data-testid="company-default-warranty"
                    />
                  </Field>
                  <Field label="Default special clauses" full>
                    <Textarea
                      rows={3}
                      value={company?.default_special_clauses || ''}
                      onChange={(e) => setCompany({ ...company, default_special_clauses: e.target.value })}
                      placeholder="Standard contract clauses (deposit handling, lien rights, dispute resolution, etc.)"
                      className="bg-[hsl(var(--muted))] border-border text-foreground resize-none text-sm"
                      data-testid="company-default-special-clauses"
                    />
                  </Field>
                  <Field label="Default change-order clause" full>
                    <Textarea
                      rows={3}
                      value={company?.default_change_order_clause || ''}
                      onChange={(e) => setCompany({ ...company, default_change_order_clause: e.target.value })}
                      placeholder="E.g. All changes to the agreed scope must be approved in writing prior to work being performed; pricing adjusted accordingly."
                      className="bg-[hsl(var(--muted))] border-border text-foreground resize-none text-sm"
                      data-testid="company-default-change-order-clause"
                    />
                  </Field>

                  <div className="sm:col-span-2 flex justify-end">
                    <Button
                      type="submit"
                      disabled={savingKey === SETTINGS_KEYS.COMPANY}
                      className="h-10 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
                      data-testid="settings-save-company"
                    >
                      {savingKey === SETTINGS_KEYS.COMPANY ? (
                        <Loader2 size={14} className="mr-2 animate-spin" />
                      ) : (
                        <Save size={14} className="mr-2" />
                      )}
                      Save company info
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CREW RATES ---------------------------------- */}
          <TabsContent value="crew">
            <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
              <CardContent className="p-4 sm:p-5">
                <SectionHeader
                  Icon={HardHat}
                  eyebrow="Labor"
                  title="Crew hourly rates"
                  subtitle="Drives Crew Clock cost calculations and Job Tracking labor totals."
                  action={
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleAddCrew}
                      className="h-9 border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
                      data-testid="settings-add-crew"
                    >
                      <Plus size={14} className="mr-2" />
                      Add member
                    </Button>
                  }
                />

                {/* Search box — filters the roster by name or role. */}
                <div className="relative mt-2 mb-3">
                  <Search
                    size={14}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <Input
                    value={crewSearch}
                    onChange={(e) => setCrewSearch(e.target.value)}
                    placeholder={t('settings.crew.searchPlaceholder', {
                      defaultValue: 'Search crew by name or role…',
                    })}
                    className="h-9 pl-9 bg-[hsl(var(--muted))] border-border text-foreground text-sm"
                    data-testid="settings-crew-search"
                  />
                  {crewSearch ? (
                    <button
                      type="button"
                      onClick={() => setCrewSearch('')}
                      aria-label="Clear search"
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs"
                    >
                      Clear
                    </button>
                  ) : null}
                </div>

                <div
                  className="flex items-center justify-between gap-2 mb-3"
                  data-testid="settings-crew-filterbar"
                >
                  <div className="text-[11px] text-muted-foreground">
                    {(() => {
                      const total = (crew || []).length;
                      const active = (crew || []).filter(isActiveCrew).length;
                      return t('settings.crew.rosterSummary', {
                        active,
                        total,
                      });
                    })()}
                  </div>
                  <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                    <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                      {t('settings.crew.showInactive')}
                    </span>
                    <Switch
                      checked={showInactive}
                      onCheckedChange={setShowInactive}
                      data-testid="settings-show-inactive-toggle"
                    />
                  </label>
                </div>

                <div className="space-y-3" data-testid="settings-crew-list">
                  {(() => {
                    const q = crewSearch.trim().toLowerCase();
                    const matches = (crew || [])
                      .filter((c) => showInactive || isActiveCrew(c))
                      .filter(
                        (c) =>
                          !q ||
                          (c.name || '').toLowerCase().includes(q) ||
                          (c.role || '').toLowerCase().includes(q)
                      );
                    if (matches.length === 0 && q) {
                      return (
                        <div
                          className="py-8 text-center text-sm text-muted-foreground"
                          data-testid="settings-crew-search-empty"
                        >
                          {t('settings.crew.searchEmpty', {
                            defaultValue: 'No crew match',
                          })}{' '}
                          &ldquo;{crewSearch.trim()}&rdquo;
                        </div>
                      );
                    }
                    return matches.map((c) => {
                    const portal = portalRoster?.[c.id] || null;
                    const code = portal?.code || '';
                    const link = code ? buildCrewSmsLink(code) : '';
                    const smsBody = code
                      ? buildCrewSmsBody({
                          crewName: c.name,
                          code,
                          companyName: company?.name,
                        })
                      : '';
                    const isBusy = pinBusyId === c.id;
                    // Find this member's index in the FULL list (not the
                    // filtered view) so input handlers patch the right row.
                    const idx = (crew || []).findIndex((x) => x.id === c.id);
                    const statusLabel = t(`settings.crew.status.${c.status}`, {
                      defaultValue: c.status,
                    });
                    const isInactive = !isActiveCrew(c);
                    return (
                      <div
                        key={c.id}
                        className={cn(
                          'border border-border rounded-md bg-[hsl(var(--muted))]/40 px-3 py-2 space-y-2',
                          isInactive && 'opacity-75'
                        )}
                        data-testid={`settings-crew-row-${c.id}`}
                      >
                        <div className="flex items-center justify-between gap-2 -mb-1">
                          <Badge
                            variant="outline"
                            className={cn(
                              'uppercase text-[10px] tracking-[0.18em] border',
                              STATUS_TONE[c.status] || STATUS_TONE.inactive
                            )}
                            data-testid={`settings-crew-status-badge-${c.id}`}
                          >
                            {statusLabel}
                            {c.status_effective_date ? (
                              <span className="ml-1.5 text-[9px] opacity-75 tabular-nums">
                                · {c.status_effective_date}
                              </span>
                            ) : null}
                          </Badge>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => openProfileSheet(c)}
                            className="h-7 border-border hover:bg-[hsl(var(--muted))] text-[11px] uppercase tracking-[0.14em]"
                            data-testid={`settings-crew-edit-${c.id}`}
                          >
                            <SettingsIcon size={11} className="mr-1.5" />
                            {t('settings.crew.editProfile')}
                          </Button>
                        </div>
                        <div className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-12 sm:col-span-5">
                            <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                              {t('settings.crew_pin.name_label')}
                            </Label>
                            <Input
                              value={c.name}
                              onChange={(e) => {
                                const next = [...crew];
                                next[idx] = { ...c, name: e.target.value };
                                setCrew(next);
                              }}
                              className="h-9 bg-[hsl(var(--background))] border-border text-foreground mt-1"
                              data-testid={`settings-crew-name-${c.id}`}
                            />
                          </div>
                          <div className="col-span-7 sm:col-span-4">
                            <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                              {t('settings.crew_pin.role_label')}
                            </Label>
                            <Input
                              value={c.role || ''}
                              onChange={(e) => {
                                const next = [...crew];
                                next[idx] = { ...c, role: e.target.value };
                                setCrew(next);
                              }}
                              className="h-9 bg-[hsl(var(--background))] border-border text-foreground mt-1"
                            />
                          </div>
                          <div className="col-span-4 sm:col-span-2">
                            <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                              {t('settings.crew_pin.rate_label')}
                            </Label>
                            <Input
                              type="number"
                              inputMode="decimal"
                              min="0"
                              step="1"
                              value={c.rate}
                              onChange={(e) => {
                                const next = [...crew];
                                next[idx] = { ...c, rate: Number(e.target.value) || 0 };
                                setCrew(next);
                              }}
                              className="h-9 bg-[hsl(var(--background))] border-border text-foreground mt-1 tabular-nums"
                              data-testid={`settings-crew-rate-${c.id}`}
                            />
                          </div>
                          <div className="col-span-1 flex justify-end">
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              onClick={() => handleRemoveCrew(c.id)}
                              aria-label={t('settings.crew_pin.remove_aria')}
                              className="h-9 w-9 mt-5 text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10"
                              data-testid={`settings-crew-remove-${c.id}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>

                        {/* Crew vs Sub. A sub's hours are stamped as sub labor
                            when logged (migration 084): shown separately in
                            job costing and kept out of PQ's labor comps. */}
                        <div
                          className="flex flex-wrap items-end gap-2"
                          data-testid={`settings-crew-type-${c.id}`}
                        >
                          <div className="inline-flex rounded-md border border-border overflow-hidden">
                            {['crew', 'sub'].map((kind) => (
                              <button
                                key={kind}
                                type="button"
                                onClick={() => {
                                  const next = [...crew];
                                  next[idx] = { ...c, type: kind };
                                  setCrew(next);
                                }}
                                className={cn(
                                  'px-3 h-8 text-[11px] uppercase tracking-[0.14em] transition-colors',
                                  c.type === kind
                                    ? 'bg-[hsl(var(--primary))] text-primary-foreground'
                                    : 'bg-[hsl(var(--background))] text-muted-foreground hover:text-foreground'
                                )}
                                aria-pressed={c.type === kind}
                                data-testid={`settings-crew-type-${kind}-${c.id}`}
                              >
                                {kind === 'crew'
                                  ? t('settings.crew.type_crew', { defaultValue: 'Crew' })
                                  : t('settings.crew.type_sub', { defaultValue: 'Subcontractor' })}
                              </button>
                            ))}
                          </div>
                          {c.type === 'sub' ? (
                            <div className="flex-1 min-w-[160px]">
                              <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                {t('settings.crew.company_label', { defaultValue: 'Company' })}
                              </Label>
                              <Input
                                value={c.company || ''}
                                onChange={(e) => {
                                  const next = [...crew];
                                  next[idx] = { ...c, company: e.target.value };
                                  setCrew(next);
                                }}
                                placeholder={t('settings.crew.company_placeholder', {
                                  defaultValue: 'Their business name',
                                })}
                                className="h-8 bg-[hsl(var(--background))] border-border text-foreground mt-1"
                                data-testid={`settings-crew-company-${c.id}`}
                              />
                            </div>
                          ) : null}
                        </div>

                        {/* --- PIN + SMS provisioning block --- */}
                        <div
                          className="rounded-md border border-dashed border-[hsl(var(--hairline))] bg-[hsl(var(--background))]/60 px-3 py-2.5"
                          data-testid={`settings-crew-pin-block-${c.id}`}
                        >
                          {code ? (
                            <div className="flex flex-col gap-2.5">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className="h-7 w-7 rounded-md border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 flex items-center justify-center shrink-0">
                                    <KeyRound size={13} className="text-[hsl(var(--primary))]" />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                                      {t('settings.crew_pin.portal_pin')}
                                    </div>
                                    <div className="text-base font-semibold tracking-[0.24em] text-foreground tabular-nums" data-testid={`settings-crew-pin-value-${c.id}`}>
                                      {code}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => copyToClipboard(code, t('settings.crew_pin.label_pin'))}
                                    className="h-8 text-[11px] uppercase tracking-[0.14em] border-border hover:bg-[hsl(var(--muted))]"
                                    data-testid={`settings-crew-copy-pin-${c.id}`}
                                  >
                                    <Copy size={12} className="mr-1.5" />
                                    {t('settings.crew_pin.copy_pin')}
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => copyToClipboard(link, t('settings.crew_pin.label_link'))}
                                    className="h-8 text-[11px] uppercase tracking-[0.14em] border-border hover:bg-[hsl(var(--muted))]"
                                    data-testid={`settings-crew-copy-link-${c.id}`}
                                  >
                                    <LinkIcon size={12} className="mr-1.5" />
                                    {t('settings.crew_pin.copy_link')}
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => copyToClipboard(smsBody, t('settings.crew_pin.label_sms'))}
                                    className="h-8 text-[11px] uppercase tracking-[0.14em] border-border hover:bg-[hsl(var(--muted))]"
                                    data-testid={`settings-crew-copy-sms-${c.id}`}
                                  >
                                    <MessageSquare size={12} className="mr-1.5" />
                                    {t('settings.crew_pin.copy_sms')}
                                  </Button>
                                  <a
                                    href={buildCrewSmsDeepLink({ body: smsBody })}
                                    className="inline-flex items-center justify-center h-8 px-3 rounded-md text-[11px] uppercase tracking-[0.14em] bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                                    data-testid={`settings-crew-open-sms-${c.id}`}
                                  >
                                    <MessageSquare size={12} className="mr-1.5" />
                                    {t('settings.crew_pin.open_sms')}
                                  </a>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleRotateCrewPin(c)}
                                    disabled={isBusy}
                                    className="h-8 text-[11px] uppercase tracking-[0.14em] text-muted-foreground hover:bg-[hsl(var(--muted))]"
                                    data-testid={`settings-crew-rotate-${c.id}`}
                                  >
                                    {isBusy ? (
                                      <Loader2 size={12} className="mr-1.5 animate-spin" />
                                    ) : (
                                      <RefreshCw size={12} className="mr-1.5" />
                                    )}
                                    {t('settings.crew_pin.rotate')}
                                  </Button>
                                </div>
                              </div>
                              <div
                                className="text-[11px] text-muted-foreground font-mono break-all bg-[hsl(var(--muted))]/40 border border-border rounded px-2 py-1.5"
                                data-testid={`settings-crew-link-${c.id}`}
                              >
                                {link}
                              </div>
                              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                {t('settings.crew_pin.sms_preview_label')}
                              </div>
                              <div
                                className="text-xs text-foreground/90 bg-[hsl(var(--card))] border border-border rounded px-3 py-2 leading-relaxed"
                                data-testid={`settings-crew-sms-preview-${c.id}`}
                              >
                                {smsBody}
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="h-7 w-7 rounded-md border border-border bg-[hsl(var(--muted))] flex items-center justify-center shrink-0">
                                  <KeyRound size={13} className="text-muted-foreground" />
                                </div>
                                <div className="min-w-0">
                                  <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                                    {t('settings.crew_pin.portal_pin')}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {t('settings.crew_pin.no_pin_yet')}
                                  </div>
                                </div>
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => handleProvisionCrewPin(c)}
                                disabled={isBusy || !c.name?.trim()}
                                className="h-8 text-[11px] uppercase tracking-[0.14em] bg-primary text-primary-foreground hover:bg-primary/90"
                                data-testid={`settings-crew-generate-pin-${c.id}`}
                              >
                                {isBusy ? (
                                  <Loader2 size={12} className="mr-1.5 animate-spin" />
                                ) : (
                                  <KeyRound size={12} className="mr-1.5" />
                                )}
                                {t('settings.crew_pin.generate_pin')}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                    });
                  })()}
                </div>

                <div className="flex justify-end mt-4">
                  <Button
                    type="button"
                    onClick={handleSaveCrew}
                    disabled={savingKey === SETTINGS_KEYS.CREW_RATES}
                    className="h-10 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
                    data-testid="settings-save-crew"
                  >
                    {savingKey === SETTINGS_KEYS.CREW_RATES ? (
                      <Loader2 size={14} className="mr-2 animate-spin" />
                    ) : (
                      <Save size={14} className="mr-2" />
                    )}
                    Save crew rates
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* DEFAULTS ---------------------------------- */}
          <TabsContent value="defaults">
            <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
              <CardContent className="p-4 sm:p-5">
                <SectionHeader
                  Icon={SettingsIcon}
                  eyebrow="Pricing & terms"
                  title="Project defaults"
                  subtitle="Default markup, payment schedule, tax, and invoice terms."
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Default markup %" testId="defaults-markup">
                    <Input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="1"
                      value={defaults?.markup_pct ?? 0}
                      onChange={(e) => setDefaults({ ...defaults, markup_pct: Number(e.target.value) || 0 })}
                      className="h-10 bg-[hsl(var(--muted))] border-border text-foreground tabular-nums"
                      data-testid="defaults-markup-input"
                    />
                  </Field>
                  <Field label="Default tax %">
                    <Input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.1"
                      value={defaults?.default_tax_pct ?? 0}
                      onChange={(e) => setDefaults({ ...defaults, default_tax_pct: Number(e.target.value) || 0 })}
                      className="h-10 bg-[hsl(var(--muted))] border-border text-foreground tabular-nums"
                    />
                  </Field>
                  <Field label="Default payment schedule">
                    <Select
                      value={defaults?.pay_schedule || '40/30/30'}
                      onValueChange={(v) => setDefaults({ ...defaults, pay_schedule: v })}
                    >
                      <SelectTrigger
                        className="h-10 bg-[hsl(var(--muted))] border-border"
                        data-testid="defaults-pay-schedule"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[hsl(var(--card))] border-border">
                        {(defaults?.pay_schedule_options || DEFAULT_DEFAULTS.pay_schedule_options).map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Default invoice due (days)">
                    <Input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      value={defaults?.default_invoice_due_days ?? 14}
                      onChange={(e) => setDefaults({ ...defaults, default_invoice_due_days: Number(e.target.value) || 0 })}
                      className="h-10 bg-[hsl(var(--muted))] border-border text-foreground tabular-nums"
                    />
                  </Field>
                </div>

                {/* Store rebate rates — auto-applied % per store on receipts.
                    Only Menards has a standing rebate; Lowe's/Home Depot stay
                    0 unless this contractor is enrolled in a program (Lowe's
                    card 5%, HD Pro Xtra 2%). A printed rebate amount scanned
                    off a receipt always overrides the rate. */}
                <div className="mt-5">
                  <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground mb-1">
                    Store rebate rates (%)
                  </div>
                  <p className="text-[11px] text-muted-foreground mb-3">
                    Auto-applied to receipts by store. Menards&apos; 11% is standard.
                    Leave Lowe&apos;s / Home Depot at 0 unless you&apos;re enrolled in
                    their programs (Lowe&apos;s card 5%, Pro Xtra rebate 2%) — a
                    rebate printed on a receipt always wins over these rates.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {['Menards', 'Lowes', 'Home Depot', 'Other'].map((s) => (
                      <Field key={s} label={s} testId={`defaults-rebate-${s.toLowerCase().replace(/\s+/g, '-')}`}>
                        <Input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          max="100"
                          step="0.5"
                          value={defaults?.store_rebates?.[s] ?? DEFAULT_DEFAULTS.store_rebates[s]}
                          onChange={(e) =>
                            setDefaults({
                              ...defaults,
                              store_rebates: {
                                ...DEFAULT_DEFAULTS.store_rebates,
                                ...(defaults?.store_rebates || {}),
                                [s]: Math.min(100, Math.max(0, Number(e.target.value) || 0)),
                              },
                            })
                          }
                          className="h-10 bg-[hsl(var(--muted))] border-border text-foreground tabular-nums"
                        />
                      </Field>
                    ))}
                  </div>
                </div>

                {/* Home Depot Pro Xtra tracker — opt-in progress toward the
                    program's qualifying spend threshold, shown on the Rebate
                    Tracker page from scanned HD receipts (pre-tax). */}
                <div className="mt-5">
                  <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground mb-1">
                    Home Depot Pro Xtra tracker
                  </div>
                  <p className="text-[11px] text-muted-foreground mb-3">
                    Pro Xtra pays a % back once your pre-tax Home Depot spend
                    clears the program minimum for the period. Turn this on to
                    see progress toward that threshold on the Rebate Tracker.
                  </p>
                  <ToggleRow
                    label="Track Pro Xtra qualifying spend"
                    value={!!defaults?.pro_xtra?.enabled}
                    onChange={(v) =>
                      setDefaults({
                        ...defaults,
                        pro_xtra: {
                          ...DEFAULT_DEFAULTS.pro_xtra,
                          ...(defaults?.pro_xtra || {}),
                          enabled: v,
                        },
                      })
                    }
                    testId="defaults-proxtra-enabled"
                  />
                  {defaults?.pro_xtra?.enabled ? (
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <Field label="Qualifying spend ($)" testId="defaults-proxtra-threshold">
                        <Input
                          type="number"
                          inputMode="numeric"
                          min="0"
                          step="500"
                          value={defaults?.pro_xtra?.threshold_usd ?? DEFAULT_DEFAULTS.pro_xtra.threshold_usd}
                          onChange={(e) =>
                            setDefaults({
                              ...defaults,
                              pro_xtra: {
                                ...DEFAULT_DEFAULTS.pro_xtra,
                                ...(defaults?.pro_xtra || {}),
                                threshold_usd: Math.max(0, Number(e.target.value) || 0),
                              },
                            })
                          }
                          className="h-10 bg-[hsl(var(--muted))] border-border text-foreground tabular-nums"
                        />
                      </Field>
                      <Field label="Rebate period" testId="defaults-proxtra-period">
                        <Select
                          value={defaults?.pro_xtra?.period || 'semiannual'}
                          onValueChange={(v) =>
                            setDefaults({
                              ...defaults,
                              pro_xtra: {
                                ...DEFAULT_DEFAULTS.pro_xtra,
                                ...(defaults?.pro_xtra || {}),
                                period: v,
                              },
                            })
                          }
                        >
                          <SelectTrigger className="h-10 bg-[hsl(var(--muted))] border-border">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[hsl(var(--card))] border-border">
                            <SelectItem value="semiannual">Semi-annual (Jan–Jun / Jul–Dec)</SelectItem>
                            <SelectItem value="annual">Annual (calendar year)</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                    </div>
                  ) : null}
                </div>
                <div className="flex justify-end mt-4">
                  <Button
                    type="button"
                    onClick={handleSaveDefaults}
                    disabled={savingKey === SETTINGS_KEYS.DEFAULTS}
                    className="h-10 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
                    data-testid="settings-save-defaults"
                  >
                    {savingKey === SETTINGS_KEYS.DEFAULTS ? (
                      <Loader2 size={14} className="mr-2 animate-spin" />
                    ) : (
                      <Save size={14} className="mr-2" />
                    )}
                    Save defaults
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* NOTIFICATIONS ---------------------------------- */}
          <TabsContent value="notifications" className="space-y-4">
            <DailyRemindersCard />
            <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
              <CardContent className="p-4 sm:p-5">
                <SectionHeader
                  Icon={Bell}
                  eyebrow="Alerts"
                  title="Notification preferences"
                  subtitle="Choose how the app keeps you informed."
                />
                <div className="space-y-2" data-testid="settings-notifications-list">
                  <ToggleRow
                    label="Email me when an invoice is paid"
                    value={!!notifications?.email_invoice_paid}
                    onChange={(v) => setNotifications({ ...notifications, email_invoice_paid: v })}
                    testId="notif-invoice-paid"
                  />
                  <ToggleRow
                    label="Email me when an estimate is signed"
                    value={!!notifications?.email_estimate_signed}
                    onChange={(v) => setNotifications({ ...notifications, email_estimate_signed: v })}
                    testId="notif-estimate-signed"
                  />
                  <ToggleRow
                    label="Daily overdue summary email"
                    value={!!notifications?.email_overdue_summary}
                    onChange={(v) => setNotifications({ ...notifications, email_overdue_summary: v })}
                    testId="notif-overdue-summary"
                  />
                </div>
                <div className="flex justify-end mt-4">
                  <Button
                    type="button"
                    onClick={handleSaveNotifications}
                    disabled={savingKey === SETTINGS_KEYS.NOTIFICATIONS}
                    className="h-10 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
                    data-testid="settings-save-notifications"
                  >
                    {savingKey === SETTINGS_KEYS.NOTIFICATIONS ? (
                      <Loader2 size={14} className="mr-2 animate-spin" />
                    ) : (
                      <Save size={14} className="mr-2" />
                    )}
                    Save notifications
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SECURITY ---------------------------------- */}
          <TabsContent value="security" className="space-y-4">
            <ChangePasswordCard />
          </TabsContent>

          {/* ACCOUNT (owner only) ----------------------- */}
          {account?.is_owner ? (
            <TabsContent value="account" className="space-y-4">
              <AccountExportCard exp={accountExport} setExp={setAccountExport} />
              <AccountLifecycleCard
                state={account}
                onChange={setAccount}
                exp={accountExport}
                setExp={setAccountExport}
              />
            </TabsContent>
          ) : null}

          {/* LANGUAGE ----------------------------------- */}
          <TabsContent value="appearance">
            <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
              <CardContent className="p-4 sm:p-5">
                <SectionHeader
                  Icon={Palette}
                  eyebrow={t('settings.eyebrow')}
                  title={t('settings.appearance_section_title', {
                    defaultValue: 'Theme',
                  })}
                  subtitle={t('settings.appearance_section_help', {
                    defaultValue:
                      'Light, Dark, Hybrid (dark sidebar + cream workspace), or Hybrid Dark (dark sidebar + cream workspace + dark cards). The sign-in and client/crew portal screens stay in the signature dark look.',
                  })}
                />
                <ThemeToggle variant="segmented" className="flex-wrap" />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="language">
            <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
              <CardContent className="p-4 sm:p-5">
                <SectionHeader
                  Icon={Languages}
                  eyebrow={t('settings.eyebrow')}
                  title={t('settings.language_section_title')}
                  subtitle={t('settings.language_section_help')}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      {t('settings.current_language')}
                    </Label>
                    <Select
                      value={currentLang}
                      onValueChange={(code) => {
                        setLanguage(code);
                        setCurrentLang(code);
                        toast.success(t('settings.language_changed'));
                      }}
                    >
                      <SelectTrigger
                        className="h-10 mt-1 bg-[hsl(var(--muted))] border-border"
                        data-testid="settings-language-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[hsl(var(--card))] border-border">
                        {SUPPORTED_LANGUAGES.map((l) => (
                          <SelectItem
                            key={l.code}
                            value={l.code}
                            data-testid={`settings-language-option-${l.code}`}
                          >
                            {l.label} <span className="text-muted-foreground">({l.shortLabel})</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                      {(i18n.language || '').toUpperCase()}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      <div className="mt-6 flex items-center gap-2 text-[11px] text-muted-foreground">
        <ShieldCheck size={12} className="text-[hsl(var(--success))]" />
        Settings are stored in the Supabase `settings` table. Changes also cache locally so you can keep working offline.
      </div>

      {/* Employee profile sheet — opened from Crew Rates rows */}
      <EmployeeProfileSheet
        open={profileSheetOpen}
        onOpenChange={setProfileSheetOpen}
        member={profileTarget}
        onSave={handleProfileSave}
        saving={profileSaving}
        onOpenHours={(m) => {
          setHoursTarget(m);
          setHoursOpen(true);
        }}
        onOpenWriteups={(m) => {
          setWriteupsTarget(m);
          setWriteupsOpen(true);
        }}
      />
      <WriteupsSheet
        open={writeupsOpen}
        onOpenChange={setWriteupsOpen}
        crewMember={writeupsTarget}
      />
      <HoursExportDialog
        open={hoursOpen}
        onOpenChange={setHoursOpen}
        crewMember={hoursTarget}
      />
    </div>
  );
}

const Field = ({ label, children, full = false, testId }) => (
  <div className={full ? 'sm:col-span-2 space-y-1.5' : 'space-y-1.5'} data-testid={testId}>
    <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
      {label}
    </Label>
    {children}
  </div>
);

const ToggleRow = ({ label, value, onChange, testId }) => (
  <div
    className="flex items-center justify-between gap-3 border border-border rounded-md bg-[hsl(var(--muted))]/40 px-3 py-2.5"
    data-testid={testId}
  >
    <span className="text-sm text-foreground">{label}</span>
    <Switch
      checked={value}
      onCheckedChange={onChange}
      data-testid={`${testId}-switch`}
    />
  </div>
);
